import { supabase } from '../../../lib/supabase/client';
import type { User } from '../types/auth.types';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  session: any;
}

export const supabaseAuthService = {
  /**
   * Kullanıcı girişi
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (error) {
      throw error;
    }

    if (!data.user || !data.session) {
      throw new Error('Giriş başarısız');
    }

    // Supabase user'ı uygulama User formatına çevir
    const user: User = {
      id: data.user.id,
      name: data.user.user_metadata?.name || data.user.email?.split('@')[0] || 'User',
      email: data.user.email || '',
      role: data.user.user_metadata?.role || 'user',
      avatar: data.user.user_metadata?.avatar,
      createdAt: data.user.created_at,
    };

    return {
      user,
      session: data.session,
    };
  },

  /**
   * Yeni kullanıcı kaydı
   */
  async register(registerData: RegisterData): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signUp({
      email: registerData.email,
      password: registerData.password,
      options: {
        data: {
          name: registerData.name,
          role: 'user',
        },
      },
    });

    if (error) {
      throw error;
    }

    if (!data.user || !data.session) {
      throw new Error('Kayıt başarısız');
    }

    const user: User = {
      id: data.user.id,
      name: registerData.name,
      email: data.user.email || '',
      role: 'user',
      createdAt: data.user.created_at,
    };

    return {
      user,
      session: data.session,
    };
  },

  /**
   * Çıkış yap
   */
  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  },

  /**
   * Mevcut kullanıcıyı al
   */
  async getCurrentUser(): Promise<User | null> {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error || !user) {
      return null;
    }

    return {
      id: user.id,
      name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
      email: user.email || '',
      role: user.user_metadata?.role || 'user',
      avatar: user.user_metadata?.avatar,
      createdAt: user.created_at,
    };
  },

  /**
   * Mevcut session'ı al
   */
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      throw error;
    }

    return session;
  },

  /**
   * Auth state değişikliklerini dinle
   */
  onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const user: User = {
          id: session.user.id,
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
          email: session.user.email || '',
          role: session.user.user_metadata?.role || 'user',
          avatar: session.user.user_metadata?.avatar,
          createdAt: session.user.created_at,
        };
        callback(user);
      } else {
        callback(null);
      }
    });
  },
};

